import React, { useState } from 'react';

import { observer } from 'mobx-react-lite';
import { SubscriptionType, SubscriptionSyncResult } from 'stores/subscription_store';
import useStores from 'hooks/useStores';

import { PriceType, CursorType, PagedResult } from 'utils/types';
import { UserProps } from 'stores/user_store';

type SubscriptionContextType = {

    // Fetch the price list for upgrades/downgrades (perhaps signups one day)
    fetchPriceList: () => Promise<PriceType[] | null>;

    // Fetch and get one
    fetchSubscription: (uuid: string) => Promise<SubscriptionType | null>;
    getSubscription: (uuid: string) => SubscriptionType | null;

    fetchPaymentLink: (price: string) => Promise<string | null>;
    syncCheckoutSession: (csi: string) => Promise<UserProps | null>;

    // Fetch and get list by identity
    fetchListByIdentitySubscription: (identity_uuid: string) => Promise<SubscriptionType[]>;
    getListByUserSubscription: (user_uuid: string) => SubscriptionType[];


    // manager list
    fetchListByUserViaSupport: (identity: string, user_uuid: string, cursor?: CursorType) => Promise<PagedResult<SubscriptionType>>;
    supportSubscriptionChange: (identity: string, uuid: string, new_price_id: string) => Promise<SubscriptionType>;
    supportSubscriptionCancel: (identity: string, uuid: string) => Promise<SubscriptionType>;
    subscriptionSync: (identity: string, user_uuid: string) => Promise<SubscriptionSyncResult | null>;
    subscriptionSyncRecurly: (identity: string, recurly_account_code: string) => Promise<PagedResult<SubscriptionType>>;

    // Contains all known Subscription items
    items: SubscriptionType[];
}

export const SubscriptionContext = React.createContext<SubscriptionContextType>({
    fetchPriceList: () => { throw 'Not Implemented' },
    fetchSubscription: () => { throw 'Not Implemented' },
    getSubscription: () => { throw 'Not Implemented' },
    fetchPaymentLink: () => { throw 'Not Implemented' },
    syncCheckoutSession: () => { throw 'Not Implemented' },
    fetchListByIdentitySubscription: () => { throw 'Not Implemented' },
    getListByUserSubscription: () => { throw 'Not Implemented' },
    fetchListByUserViaSupport: () => { throw 'Not Implemented' },
    supportSubscriptionChange: () => { throw 'Not Implemented' },
    supportSubscriptionCancel: () => { throw 'Not Implemented' },
    subscriptionSync: () => { throw 'Not Implemented' },
    subscriptionSyncRecurly: () => { throw 'Not Implemented' },

    items: [] as SubscriptionType[],
});




export const SubscriptionProvider = observer(({ children }: { children: React.ReactNode }) => {
    const [items, setSubscription] = useState([] as SubscriptionType[]);
    const { subscriptionStore } = useStores();


    const fetchSubscription = async (uuid: string) => {
        // Autogenerated
        return subscriptionStore.fetchSubscription(uuid);
    };

    const fetchPriceList = async () => {
        return subscriptionStore.fetchPriceList();
    };

    const getSubscription = (uuid: string) => {
        // Autogenerated
        return items.find((item) => item.uuid === uuid) || null;
    };

    const fetchListByIdentitySubscription = async (identity_uuid: string) => {
        console.log("fetchListByIdentitySubscription: ")
        return subscriptionStore.fetchListByIdentitySubscription(identity_uuid)
            .then(() => {
                setSubscription(subscriptionStore.items);
                return []; // load them below
            });
    };

    const getListByUserSubscription = (user_uuid: string) => {
        return items.filter((item) => item.user === user_uuid);
    };


    const fetchListByUserViaSupport = async (identity: string, user_uuid: string, cursor?: CursorType) => {
        return subscriptionStore.fetchListByUserViaSupport(identity, user_uuid, cursor);
    };

    const supportSubscriptionChange = async (identity: string, uuid: string, new_price_id: string) => {
        return subscriptionStore.supportSubscriptionChange(identity, uuid, new_price_id);
        };


    const supportSubscriptionCancel = async (identity: string, uuid: string) => {
        return subscriptionStore.supportSubscriptionCancel(identity, uuid);
        };

    const subscriptionSync = async (identity: string, user_uuid: string) => {
        return subscriptionStore.subscriptionSync(identity, user_uuid);
    };

    const subscriptionSyncRecurly = async (identity: string, recurly_account_code: string) => {
        return subscriptionStore.subscriptionSyncRecurly(identity, recurly_account_code);
    };

    const data = {
        fetchPriceList,
        fetchSubscription,
        getSubscription,
        fetchListByIdentitySubscription,
        getListByUserSubscription,
        fetchListByUserViaSupport,
        supportSubscriptionChange,
        supportSubscriptionCancel,
        subscriptionSync,
        fetchPaymentLink: subscriptionStore.fetchPaymentLink,
        syncCheckoutSession: subscriptionStore.syncCheckoutSession,
        subscriptionSyncRecurly,

        items,
    };

    return (
        <SubscriptionContext.Provider value={data}>{ children }</SubscriptionContext.Provider>
    );

});
